import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import UserDropdown from '../../../../../components/common/UserDropdown';
import { useGetApprovalLimitGroupByIdQuery } from '../../../../../api/endpoints/approvalLimitGroups';
import { formatDateOnly } from '../../../../../utils/dateUtils';

const defaultFormState = {
    id: null,
    groupName: '',
    approvalPersons: []
};
const ApprovalGroupsForm = ({ visible, onHide, onSubmit, editMode = false, selectedItem = null }) => {
    const { th } = useLanguage();

    const [formData, setFormData] = useState(defaultFormState);
    const [localSubmitted, setLocalSubmitted] = useState(false);
    const toast = useRef(null);

    const { data: groupDetails, isLoading: isLoadingDetails } = useGetApprovalLimitGroupByIdQuery(selectedItem?.id, { skip: !editMode || !selectedItem?.id || !visible });

    useEffect(() => {
        if (!visible) {
            setFormData(defaultFormState);
            setLocalSubmitted(false);
        }
    }, [visible]);

    useEffect(() => {
        if (visible && editMode && groupDetails) {
            setFormData({
                id: groupDetails.id,
                groupName: groupDetails.groupName || '',
                approvalPersons: groupDetails.approvalPersons
                    ? groupDetails.approvalPersons.map((person) => ({
                          id: person.userId,
                          name: person.userName || person.name
                      }))
                    : []
            });
        } else if (visible && !editMode) {
            setFormData(defaultFormState);
        }
    }, [visible, editMode, groupDetails]);

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        setLocalSubmitted(true);

        const validationErrors = [];
        if (!formData.groupName?.trim()) {
            validationErrors.push(th('approvalGroups.groupNameRequired'));
        }

        if (validationErrors.length > 0) {
            validationErrors.forEach((error, index) => {
                setTimeout(() => {
                    toast.current?.show({
                        severity: 'error',
                        summary: th('approvalGroups.missingField'),
                        detail: error,
                        life: 4000
                    });
                }, index * 200);
            });
            return;
        }

        const submitData = {
            id: formData.id,
            groupName: formData.groupName.trim(),
            userIds:
                formData.approvalPersons?.map((person) => ({
                    id: person.id,
                    name: person.name
                })) || []
        };

        if (onSubmit) {
            onSubmit(submitData);
        } else {
            console.log('❌ onSubmit function is not provided');
        }
    };

    const handleClose = () => {
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={handleClose} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                style={{ width: '90vw', maxWidth: '650px' }}
                header={editMode ? th('approvalGroups.editGroup') : th('approvalGroups.newGroup')}
                modal
                className="p-fluid"
                footer={footer}
                onHide={handleClose}
                breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            >
                <div className="p-fluid">
                    <div className="field">
                        <label htmlFor="groupName" className="font-bold">
                            {th('approvalGroups.groupName')} <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="groupName"
                            value={formData.groupName}
                            onChange={(e) => handleFieldChange('groupName', e.target.value)}
                            required
                            autoFocus
                            className={localSubmitted && !formData.groupName?.trim() ? 'p-invalid' : ''}
                            placeholder={th('approvalGroups.groupNamePlaceholder')}
                            disabled={isLoadingDetails}
                        />
                        {localSubmitted && !formData.groupName?.trim() && <small className="p-error">{th('approvalGroups.groupNameRequired')}</small>}
                    </div>

                    <div className="field mt-3">
                        <label htmlFor="approvalPersons" className="font-bold">
                            {th('common.members')}
                        </label>
                        <UserDropdown
                            value={formData.approvalPersons}
                            onChange={(selectedUsers) => {
                                handleFieldChange('approvalPersons', selectedUsers);
                            }}
                            placeholder={th('approvalGroups.approvalPersonsPlaceholder')}
                            disabled={isLoadingDetails}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default ApprovalGroupsForm;
